import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { SearchValues } from '@/components/custom/erp/data-table-v2';
import { useHoaDonBanDichVu } from '@/hooks/queries/useHoaDonBanDichVu';
import { useHoaDonBanDichVuExport } from './hooks';

interface HoaDonBanDichVuSearchContextValue {
  searchState: {
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  };
  handleSearchChange: (values: SearchValues) => Promise<void>;
  resolvedRows: BanHangRecord[];
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
}

const HoaDonBanDichVuSearchContext = createContext<HoaDonBanDichVuSearchContextValue | null>(null);

export function useHoaDonBanDichVuSearchContext() {
  const context = useContext(HoaDonBanDichVuSearchContext);
  if (!context) {
    throw new Error('useHoaDonBanDichVuSearchContext must be used within HoaDonBanDichVuSearchProvider');
  }
  return context;
}

export function HoaDonBanDichVuSearchProvider({ children }: { children: ReactNode }) {
  const [searchState, setSearchState] = useState<{
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  }>({
    filterParams: {},
    searchResults: [],
    isSearching: false
  });

  const { filterHoaDonBanDichVus } = useHoaDonBanDichVu();
  const { exportToExcel, canExport, isExporting } = useHoaDonBanDichVuExport();

  const handleSearchChange = useCallback(async (values: SearchValues) => {
    const nextHasFilters = Object.keys(values || {}).length > 0;

    if (!nextHasFilters) {
      setSearchState({
        filterParams: {},
        searchResults: [],
        isSearching: false
      });
      return;
    }

    setSearchState(prev => ({
      ...prev,
      filterParams: values,
      isSearching: false,
      searchResults: []
    }));
  }, []);

  // Fetch filtered data when filterParams change
  useEffect(() => {
    const fetchFilteredData = async () => {
      if (!searchState.filterParams || Object.keys(searchState.filterParams).length === 0) {
        return;
      }

      setSearchState(prev => ({ ...prev, isSearching: true }));

      try {
        const transformedParams = Object.entries(searchState.filterParams).reduce((acc, [key, value]) => {
          acc[key] = Array.isArray(value) ? value : [value];
          return acc;
        }, {} as BanHangSearchParams);

        const res = await filterHoaDonBanDichVus(transformedParams);
        setSearchState(prev => ({
          ...prev,
          searchResults: res || [],
          isSearching: false
        }));
      } catch (error) {
        console.error('Error fetching filtered data:', error);
        setSearchState(prev => ({
          ...prev,
          searchResults: [],
          isSearching: false
        }));
      }
    };

    fetchFilteredData();
  }, [searchState.filterParams, filterHoaDonBanDichVus]);

  const resolvedRows = searchState.searchResults;

  return (
    <HoaDonBanDichVuSearchContext.Provider
      value={{
        searchState,
        handleSearchChange,
        resolvedRows,
        exportToExcel,
        canExport,
        isExporting
      }}
    >
      {children}
    </HoaDonBanDichVuSearchContext.Provider>
  );
}
